/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FLOX_LOGO_BLUE_SRC,
  LOGIN_WITH_CREDENTIALS_URL,
  TWO_FACTOR_TRUST_ID_KEY,
} from '@constants';
import { useDocumentTitle } from 'usehooks-ts';
import { AxiosResponse, AxiosError } from 'axios';
import { useAuthHttp } from '@hooks';
import { useAuth } from '@context';
import { LoginWith2FaForm } from '@forms/LoginWith2FAForm/LoginWith2FaForm';
import { Modal, ModalContainer, ModalHeader } from '@components/Modal';
import {
  ExclamationTriangleIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Input } from '../../tailwindComponents/Input';
import { Button } from '../../tailwindComponents/Button';
import { LoginValues, LoginResponse, LoginErrorResponse } from './types';

const loginValidationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address.')
    .required('An email address is required.'),
  password: Yup.string().required('A password is required.'),
});

export function Login() {
  const navigate = useNavigate();
  const { logIn, setUser, forgotPassword } = useAuth();
  const authHttp = useAuthHttp();
  const [showLoginError, setShowLoginError] = useState(false);
  const [loginErrorText, setLoginErrorText] = useState('');
  const [searchParams] = useSearchParams();
  const [is2FaModalOpen, setIs2faModalOpen] = useState(false);
  const [twoFactorId, setTwoFactorId] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useDocumentTitle('FLOX - Log in');

  const redirect = searchParams.get('redirect') || '';

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginValues>({
    resolver: yupResolver(loginValidationSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    setIs2faModalOpen(!!twoFactorId);
  }, [twoFactorId]);

  const handleLoginResponse = (loginResponse: LoginResponse): void => {
    if (loginResponse.twoFactorId) {
      localStorage.removeItem(TWO_FACTOR_TRUST_ID_KEY);
      setTwoFactorId(loginResponse.twoFactorId);
      return;
    }

    if (!loginResponse.accessToken) {
      setLoginErrorText('Something went wrong.');
      setShowLoginError(true);
      return;
    }

    if (loginResponse.twoFactorTrustId) {
      localStorage.setItem(
        TWO_FACTOR_TRUST_ID_KEY,
        loginResponse.twoFactorTrustId
      );
    }

    setIs2faModalOpen(false);
    setUser(loginResponse.user);
    logIn(
      loginResponse.user,
      {
        accessToken: loginResponse.accessToken,
        refreshToken: loginResponse.refreshToken,
        tokenExpiry: loginResponse.tokenExpiry,
        notificationToken: loginResponse.notificationToken,
      },
      redirect || '/noticeboard'
    );
  };

  const onSubmit = async (values: LoginValues): Promise<void> => {
    setLoginErrorText('');
    setShowLoginError(false);

    await authHttp
      .post(LOGIN_WITH_CREDENTIALS_URL, {
        email: values.email,
        password: values.password,
        twoFactorTrustId:
          localStorage.getItem(TWO_FACTOR_TRUST_ID_KEY) ?? undefined,
      })
      .then((res: AxiosResponse<LoginResponse>) => {
        handleLoginResponse(res.data);
      })
      .catch((err: AxiosError<LoginErrorResponse>) => {
        if (err?.response?.status === 401) {
          reset();
          setLoginErrorText('Email and/or password was not recognised.');
        } else {
          setLoginErrorText('Something went wrong.');
        }
        setShowLoginError(true);
      });
  };

  return (
    <main className='flex min-h-screen w-full'>
      {/* Image Panel */}
      <section
        className='hidden md:block md:w-1/2 min-h-screen bg-cover bg-no-repeat bg-center transition-all duration-300'
        style={{ backgroundImage: 'url(/images/bg-login.png)' }}
      />

      {/* Form Panel */}
      <section className='flex-1 flex items-center justify-center bg-white px-4 py-12'>
        <div className='w-full max-w-md'>
          {/* Logo */}
          <div className='flex justify-center mb-8'>
            <img
              src={FLOX_LOGO_BLUE_SRC}
              alt='Flox logo'
              className='h-14 w-auto'
            />
          </div>

          {/* Title */}
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              Welcome back
            </h1>
            <p className='text-gray-500'>Sign in to Flox Platform</p>
          </div>

          {/* Login Card */}
          <div className='bg-white rounded-2xl shadow-lg border border-gray-100 p-8'>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Email Field */}
              <div className='mb-4'>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Email address
                </label>
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <div className='relative'>
                      <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                        <EnvelopeIcon className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        {...field}
                        type='email'
                        id='email'
                        placeholder='you@company.com'
                        autoComplete='email'
                        disabled={isSubmitting}
                        className={`block w-full rounded-lg border py-3 pl-10 pr-3 text-sm transition-colors ${
                          errors.email
                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                            : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                        } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                        onFocus={() => {
                          if (showLoginError) {
                            setLoginErrorText('');
                            setShowLoginError(false);
                          }
                        }}
                      />
                    </div>
                  )}
                />
                {errors.email && (
                  <p className='mt-1.5 text-sm text-red-600'>
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className='mb-6'>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-gray-700 mb-2'
                >
                  Password
                </label>
                <Controller
                  name='password'
                  control={control}
                  render={({ field }) => (
                    <div className='relative'>
                      <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'>
                        <LockClosedIcon className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        {...field}
                        type={showPassword ? 'text' : 'password'}
                        id='password'
                        placeholder='••••••••••••'
                        autoComplete='current-password'
                        disabled={isSubmitting}
                        className={`block w-full rounded-lg border py-3 pl-10 pr-10 text-sm transition-colors ${
                          errors.password
                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                            : 'border-gray-200 bg-gray-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:bg-white'
                        } focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      />
                      <button
                        type='button'
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute inset-y-0 right-0 flex items-center pr-3'
                      >
                        {showPassword
                          ? <EyeSlashIcon className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                          : <EyeIcon className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        }
                      </button>
                    </div>
                  )}
                />
                {errors.password && (
                  <p className='mt-1.5 text-sm text-red-600'>
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type='submit'
                intent='primary'
                size='lg'
                disabled={!isValid || isSubmitting}
                className='w-full'
                label={isSubmitting ? 'Signing in...' : 'Sign in'}
              />

              {/* Error Message - shown below button for visibility */}
              {showLoginError && (
                <div className='rounded-lg bg-yellow-50 p-4 mt-4'>
                  <div className='flex'>
                    <ExclamationTriangleIcon className='h-5 w-5 text-yellow-400 flex-shrink-0' />
                    <p className='ml-3 text-sm text-yellow-700'>
                      {loginErrorText}
                    </p>
                  </div>
                </div>
              )}

              {/* Secured By */}
              <div className='flex items-center justify-center gap-2 mt-6 text-gray-400'>
                <ShieldCheckIcon className='h-4 w-4' />
                <span className='text-xs'>
                  Secured by FLOX Enterprise Authentication
                </span>
              </div>
            </form>
          </div>

          {/* Footer Links */}
          <div className='mt-8 text-center space-y-4'>
            <div className='flex justify-center items-center gap-2'>
              <span className='text-sm text-gray-500'>No account?</span>
              <button
                type='button'
                onClick={() => {
                  reset();
                  navigate('/register');
                }}
                className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline'
              >
                Create an account
              </button>
            </div>

            <button
              type='button'
              onClick={() => forgotPassword()}
              className='text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline'
            >
              Forgotten password?
            </button>

            <p className='text-sm text-gray-400 pt-4'>
              Need help? Contact{' '}
              <a
                href='mailto:support@flox.is'
                className='text-blue-600 hover:underline'
              >
                support@flox.is
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* 2FA Modal */}
      <Modal
        open={is2FaModalOpen}
        onOpenChange={(isOpen: boolean) => setIs2faModalOpen(isOpen)}
      >
        <ModalContainer preventCloseOnOutsideClick>
          <ModalHeader title='Login using 2 Factor Authentication' />
          <LoginWith2FaForm
            twoFactorId={twoFactorId}
            handleLoginResponse={handleLoginResponse}
          />
        </ModalContainer>
      </Modal>
    </main>
  );
}
